import React from 'react';

import { usePeriods } from 'components/hooks/usePeriods';

import {
  TimeLineItem,
  TimeLine,
  DatePicker,
  Alert,
  Stack,
  Grid,
  Tooltip,
  IconButton,
  ToggleButton,
  Select,
  TextField,
  ToggleButtonGroup,
  RequestQuoteIcon,
  HighlightOffIcon,
} from '@gogame/components';
import { withFormField } from '@gogame/form';
import { useChildComponent } from '@gogame/hooks-ts';
import { dateFNS, toDate } from '@gogame/intl-ts';
import { useQuery } from '@gogame/query';

import { loadCostModelCurrencies } from 'scenes/Campaigns/sources/fields';
import FinancialResultDetails from 'scenes/FinancialResults/components/FinancialResultDetails';

import {
  PERIOD,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  RATE,
  RATE_VALUE,
  RATE_CURRENCY,
  FINANCIAL_RESULT_ID,
  CURRENCY_USD,
  IS_READONLY,
  ACTIVITY_TYPE,
  ACTIVITY_TYPE_UA,
  ACTIVITY_TYPE_RE,
  ACTIVITY_TYPE_TOTAL,
  getIndicatorColor,
} from '../constants';

const createPeriod =
  ({ isSupportActivityTypes, defaultActivityType }) =>
  (props) => {
    return {
      [PERIOD]: {
        [PERIOD_START_DATE]: props?.from,
        [PERIOD_END_DATE]: props?.to,
      },
      [RATE]: {
        [RATE_CURRENCY]: CURRENCY_USD,
      },
      ...(isSupportActivityTypes && {
        [ACTIVITY_TYPE]: defaultActivityType,
      }),
    };
  };

const updatePeriod = (state, props) => {
  return {
    ...state,
    [PERIOD]: {
      [PERIOD_START_DATE]: props?.from,
      [PERIOD_END_DATE]: props?.to,
    },
  };
};

const EMPTY = [];

const CostModelPeriods = withFormField({ padding: 0 })(({
  typeId,
  isSupportActivityTypes,
  defaultActivityType,
  value = EMPTY,
  costModelIsReadonly,
  canRemovePeriod = false,
  canAddPeriod,
  finishDateRequired,
  onChange,
  error,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data: options } = useQuery(
    ['cost-model-currencies'],
    loadCostModelCurrencies
  );

  const { add, remove, setFieldValue, setPeriodFieldValue } = usePeriods({
    value,
    onChange,
    createPeriod: createPeriod({
      isSupportActivityTypes,
      defaultActivityType,
    }),
    updatePeriod,
  });
  const lastPeriodValid =
    !value.length ||
    value.slice(-1).pop()?.[PERIOD]?.[PERIOD_END_DATE] !== undefined;

  const handleValidateRate = (rate) => {
    if (!rate && typeId !== 10) {
      // "Custom Flat Fee" can be 0, other must be > 0
      return 'Rate should be > 0';
    }
  };

  return (
    <Stack>
      <TimeLine
        fullWidth={true}
        withRemove={true}
        onAdd={add}
        withAdd={canAddPeriod}
        addLabel={'Cost Period'}
        addDisabled={!lastPeriodValid}
      >
        {value?.map((costModelPeriod, i) => {
          const prev = value[i - 1];
          const next = value[i + 1];
          const costModelPeriodError = error?.[i];
          const removable = canRemovePeriod && value?.length > 1;

          const financialResultId = costModelPeriod[FINANCIAL_RESULT_ID];
          const isReadonly =
            costModelIsReadonly || costModelPeriod[IS_READONLY];

          const tooltipText =
            isReadonly && financialResultId
              ? 'This period that has approved financial result cannot be edited'
              : null;

          const handleOpenFinancialResult = () => {
            openChildComponent(FinancialResultDetails, {
              id: financialResultId,
            });
          };

          return (
            <TimeLineItem
              onRemove={() => remove(i)}
              color={getIndicatorColor(costModelPeriod?.[ACTIVITY_TYPE])}
            >
              <Grid container spacing={2} alignItems={'end'}>
                <Grid item>
                  <Tooltip title={tooltipText} placement={'bottom'}>
                    <div>
                      <TextField
                        width={85}
                        name={RATE_VALUE}
                        required={true}
                        label={'Rate'}
                        type={'number'}
                        padding={0}
                        validate={(value) => handleValidateRate(value)}
                        disabled={isReadonly}
                        value={costModelPeriod?.[RATE]?.[RATE_VALUE]}
                        onChange={(_, v) =>
                          setFieldValue(i, `${RATE}.${RATE_VALUE}`, v)
                        }
                        error={costModelPeriodError?.[RATE]?.[RATE_VALUE]}
                      />
                    </div>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <Tooltip title={tooltipText} placement={'bottom'}>
                    <div>
                      <Select
                        width={95}
                        name={RATE_CURRENCY}
                        required={true}
                        label={'Currency'}
                        options={options || EMPTY}
                        padding={0}
                        disabled={isReadonly}
                        value={costModelPeriod?.[RATE]?.[RATE_CURRENCY]}
                        onChange={(_, v) =>
                          setFieldValue(i, `${RATE}.${RATE_CURRENCY}`, v)
                        }
                        error={costModelPeriodError?.[RATE]?.[RATE_CURRENCY]}
                      />
                    </div>
                  </Tooltip>
                </Grid>
                <Grid item xs={3}>
                  <DatePicker
                    padding={0}
                    label={'Start date'}
                    fullWidth={true}
                    required={true}
                    name={PERIOD_START_DATE}
                    minDate={
                      prev
                        ? dateFNS.addDays(
                            toDate(prev[PERIOD][PERIOD_END_DATE]),
                            1
                          )
                        : undefined
                    }
                    maxDate={
                      next
                        ? dateFNS.subDays(
                            toDate(next[PERIOD][PERIOD_END_DATE]),
                            1
                          )
                        : undefined
                    }
                    onChange={(_, v) =>
                      setPeriodFieldValue(i, PERIOD_START_DATE, v)
                    }
                    value={costModelPeriod?.[PERIOD]?.[PERIOD_START_DATE]}
                    error={costModelPeriodError?.[PERIOD]?.[PERIOD_START_DATE]}
                    disabled={isReadonly}
                  />
                </Grid>
                <Grid item xs={3}>
                  <DatePicker
                    padding={0}
                    label={'End date'}
                    fullWidth={true}
                    required={finishDateRequired}
                    name={PERIOD_END_DATE}
                    minDate={dateFNS.endOfDay(
                      toDate(costModelPeriod?.[PERIOD]?.[PERIOD_START_DATE])
                    )}
                    maxDate={
                      next
                        ? dateFNS.subDays(
                            toDate(next[PERIOD][PERIOD_START_DATE]),
                            1
                          )
                        : undefined
                    }
                    onChange={(_, v) =>
                      setPeriodFieldValue(i, PERIOD_END_DATE, v)
                    }
                    value={costModelPeriod?.[PERIOD]?.[PERIOD_END_DATE]}
                    error={costModelPeriodError?.[PERIOD]?.[PERIOD_END_DATE]}
                    disabled={
                      (!finishDateRequired && !canAddPeriod) || isReadonly
                    }
                  />
                </Grid>
                {(financialResultId || isSupportActivityTypes) && (
                  <Grid item xs={2} spacing={3}>
                    {isSupportActivityTypes ? (
                      <Grid item xs={10}>
                        <ToggleButtonGroup
                          name={ACTIVITY_TYPE}
                          padding={0}
                          disabled={isReadonly}
                          fullWidth={true}
                          value={costModelPeriod?.[ACTIVITY_TYPE]}
                          onChange={(_, v) => {
                            setFieldValue(i, ACTIVITY_TYPE, v);
                          }}
                        >
                          <ToggleButton value={ACTIVITY_TYPE_UA}>
                            UA
                          </ToggleButton>
                          <ToggleButton value={ACTIVITY_TYPE_RE}>
                            RE
                          </ToggleButton>
                          <ToggleButton value={ACTIVITY_TYPE_TOTAL}>
                            Total
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>
                    ) : null}
                    {financialResultId ? (
                      <Grid item xs={2}>
                        <IconButton
                          icon={<RequestQuoteIcon />}
                          tooltip={'View financial result'}
                          onClick={handleOpenFinancialResult}
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                )}
                {removable && !financialResultId ? (
                  <Grid item>
                    <IconButton
                      icon={<HighlightOffIcon />}
                      onClick={() => remove(i)}
                      disabled={isReadonly}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </TimeLineItem>
          );
        })}
      </TimeLine>
      {canAddPeriod && !lastPeriodValid ? (
        <Alert severity={'info'}>
          To add a new period, first set the end date of the last period.
        </Alert>
      ) : null}
      {childComponent}
    </Stack>
  );
});

export default CostModelPeriods;
